import { gql } from '@apollo/client'

/**
 * Minimal Content fields for member-home SSR spotlight placement inline.
 * Omits contentFeatured, references, courseProgress, and other tile-only weight.
 */
export const CONTENT_SPOTLIGHT_SSR_INLINE = gql`
  fragment ContentSpotlightSsrInline on Content {
    id
    contentId
    contentType
    titleGroupId
    title
    path
    shortDescription(truncate: 300)
    seasonNumber
    episodeNumber
    published
    createdAt
    featureMedia {
      duration
      mediaId
      contentPolicy {
        accessCheck {
          allowed
        }
      }
      userMediaPosition {
        position
      }
    }
    previewMedia {
      duration
      mediaId
    }
    imageContentRelationships(
      language: $contentLanguage
      types: $imageType
    ) {
      type
      image {
        url
      }
    }
    contentSeries(language: $contentLanguage) {
      contentId
      title
      path
    }
  }
`

/**
 * Full tile inline Content — parity with GET_CONTENT limitedData: true on PM tab rows.
 * Uses relative path (parity with GET_CONTENT limitedData) for member-home tabContent inline.
 */
export const CONTENT_TILE_INLINE_FULL = gql`
  fragment ContentTileInlineFull on Content {
    contentAnalytic {
      commentsCountTotal
      votesUpCountTotal
      votesCountTotal
      thumbPopularityMappingId
    }
    id
    title
    path
    episodeNumber
    seasonNumber
    displayType
    studio
    contentType
    subType
    contentId
    titleGroupId
    seasonsCount (language: $contentLanguage)
    episodesCount (language: $contentLanguage, content: [{ publishedState: PUBLISHED_NOW }])
    isNew
    primaryLanguage
    languageFullText
    cast
    instructor
    published
    createdAt
    shortDescription
    ctaLabel
    ctaPath
    productionStatus
    references (types: ["genres", "centers", "yoga-duration", "yoga-style", "yoga-level", "fitness-duration", "fitness-style", "fitness-level", "meditation-duration", "meditation-style", "meditation-level", "primary-course"]) {
      id
      contentReferenceType {
        type
      }
      contentTo (language: $contentLanguage) {
        title
        contentType
        contentId
        language
        path
      }
    }
    featureMedia {
      duration
      mediaId
      mediaAssets {
        language
        mediaReferenceType
        aiMetadata(language: $learnerLevelLanguage) {
          learnerLevel {
            level
          }
        }
      }
      contentPolicy {
        shareAllowed
        accessCheck {
          allowed
          countryAllowed
          subscriptionsAllowed
          isFreeAllowed
        }
        optionIds
      }
      userMediaPosition {
        position
      }
    }
    previewMedia {
      mediaId
    }
    imageContentRelationships(
      language: $contentLanguage
      types: $imageType
    ) {
      type
      image {
        url
      }
    }
    contentSeries(language: $contentLanguage) {
      id
      contentId
      title
      path
      shortDescription
      imageContentRelationships(
        language: $contentLanguage
        types: $imageType
      ) {
        type
        image {
          url
        }
      }
    }
    contentFeatured (language: $contentLanguage, content: [{ publishedState: PUBLISHED_NOW }]) {
      content {
        contentAnalytic {
          commentsCountTotal
          votesUpCountTotal
          votesCountTotal
          thumbPopularityMappingId
        }
        id
        title
        path
        ctaProductUrl
        episodeNumber
        seasonNumber
        displayType
        contentType
        subType
        contentId
        isNew
        primaryLanguage
        languageFullText
        shortDescription
        cast
        instructor
        references (types: ["genres", "centers", "yoga-duration", "yoga-style", "yoga-level", "fitness-duration", "fitness-style", "fitness-level", "meditation-duration", "meditation-style", "meditation-level"]) {
          id
          contentReferenceType {
            type
          }
          contentTo (language: $contentLanguage) {
            title
            contentType
            contentId
            language
            path
          }
        }
        featureMedia {
          duration
          mediaId
          mediaAssets {
            language
            mediaReferenceType
            aiMetadata(language: $learnerLevelLanguage) {
              learnerLevel {
                level
              }
            }
          }
          contentPolicy {
            shareAllowed
            accessCheck {
              allowed
              countryAllowed
              subscriptionsAllowed
              isFreeAllowed
            }
            optionIds
          }
          userMediaPosition {
            position
          }
        }
        previewMedia {
          mediaId
        }
        contentCourses (language: $contentLanguage, content: [{ publishedState: PUBLISHED_NOW }]) {
          id
          contentId
          titleGroupId
        }
      }
      featuredType
    }
    courseProgress {
      state
      completionCount
      metrics {
        totalModules
        completedModules
        totalItems
        completedItems
        percentageModulesComplete
        percentageItemsComplete
      }
    }
    contentCourses (language: $contentLanguage, content: [{ publishedState: PUBLISHED_NOW }]) {
      id
      contentId
      titleGroupId
    }
  }
`
